import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { reportService } from '../../services/reportService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import ComparePicker from './ComparePicker';

// =============================================================================
// Reporting › Revenue Management › SKU Profitability.
//
// Volume → gross revenue → discounts → net revenue → cost of sale → gross
// profit, per SKU, ranked so the products carrying the business and the ones
// being sold at a loss are both on the first page.
//
// THE HEADLINE MARGIN IS OVER COSTED REVENUE ONLY, and the page says so. Not
// every SKU has been costed — CompanyA has never costed its 15kg range, which
// is two thirds of revenue. Blending uncosted revenue into the margin would
// report that business at 75% when the part it can actually see runs at 14%.
// An uncosted line shows "—" for margin, never 100%.
//
// Discounts are AGREED WITH CUSTOMERS AND CUSTOMER GROUPS, not with SKUs, so
// they are allocated pro-rata on gross revenue within the same site and period.
// The banner says so, and the server proves the allocation is complete rather
// than asserting it.
//
// The page renders straight from the dataset the API returns. A server-side
// render service (PDF/XLSX) can be added later against the same dataset without
// touching this file.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

// EXPLICIT, not the machine default. The browser's locale is the user's, not
// the tenant's, so the same report would otherwise read differently on two
// desks. Comma thousands, dot decimal. Any server-side renderer added later
// must pin the same string.
const REPORT_LOCALE = 'en-GB';

const fmt0 = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  return v < 0 ? `(${Math.abs(v).toLocaleString(REPORT_LOCALE)})` : v.toLocaleString(REPORT_LOCALE);
};
const fmt2 = (n) => {
  const v = Number(n) || 0;
  if (!v) return '—';
  const s = Math.abs(v).toLocaleString(REPORT_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const fmtPct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.22)', baseFontStyle: '700 12px' };
const T_MUTED = { textDark: '#94a3b8' };
const T_LOSS = { textDark: '#dc2626', bgCell: 'rgba(220, 38, 38, 0.10)' };
const T_UNCOSTED = { textDark: '#b45309', bgCell: 'rgba(180, 83, 9, 0.08)' };

export default function SkuProfitability() {
  const navigate = useNavigate();
  const { sectionCode } = useParams();
  const { selectedVersion, selectedSite } = useAppContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // "" = no comparison. A source key otherwise: "version:V1", "actual:2025".
  const [compare, setCompare] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getReportData('SKU_PROFIT', compare ? { compare } : {});
      setData(res.data);
    } catch (e) {
      // A refused comparison must not leave the previous comparison's columns
      // on screen labelled as the new one. Drop back to no comparison and say
      // why, rather than showing numbers that are quietly from somewhere else.
      setError(e.response?.data?.message || e.message || 'Could not run the report.');
      if (compare) setCompare('');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [compare]);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);


  const skus = useMemo(() => data?.skus || [], [data]);
  const totals = data?.totals;
  const caveats = data?.caveats;


  const columns = useMemo(() => ([
    { id: 'stock_code', title: 'SKU', width: 140 },
    { id: 'description', title: 'Description', width: 250, grow: 1 },
    { id: 'stock_group', title: 'Group', width: 120 },
    { id: 'quantity', title: 'Volume', width: 100 },
    { id: 'gross_revenue', title: 'Gross Revenue', width: 130 },
    { id: 'discount_amount', title: 'Discounts', width: 120 },
    { id: 'net_revenue', title: 'Net Revenue', width: 130 },
    { id: 'cost_of_sale', title: 'Cost of Sale', width: 130 },
    { id: 'gross_profit', title: 'Gross Profit', width: 130 },
    { id: 'margin_pct', title: 'GP %', width: 80 },
    // Below the gross profit line. Three components rather than one
    // "distribution" number, because they are three different levers.
    { id: 'secondary_cost', title: 'Delivery', width: 115 },
    { id: 'transfer_cost', title: 'Inter-depot', width: 115 },
    { id: 'warehouse_cost', title: 'Handling', width: 115 },
    { id: 'logistics_cost', title: 'Logistics', width: 120 },
    { id: 'contribution', title: 'Contribution', width: 130 },
    { id: 'contribution_pct', title: 'Cont %', width: 80 },
    { id: 'price_per_unit', title: 'Net / unit', width: 100 },
    { id: 'cost_per_unit', title: 'Cost / unit', width: 100 },
    { id: 'logistics_per_unit', title: 'Freight / unit', width: 110 },
    { id: 'profit_per_unit', title: 'Profit / unit', width: 105 },
    { id: 'contribution_per_unit', title: 'Cont / unit', width: 105 },
  ]), []);
  const colIds = useMemo(() => columns.map((c) => c.id), [columns]);

  const rowCount = skus.length + 1; // + TOTAL

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];

    if (row === skus.length) { // TOTAL
      if (!totals) return gcell.text('', { readonly: true, theme: T_TOTAL });
      if (id === 'stock_code') return gcell.text('TOTAL', { readonly: true, theme: T_TOTAL });
      if (id === 'description') {
        return gcell.text(`${skus.length} SKU${skus.length === 1 ? '' : 's'}`, { readonly: true, theme: T_TOTAL });
      }
      if (id === 'margin_pct') {
        // Over COSTED revenue only — the whole point of the caveat above.
        return gcell.text(fmtPct(totals.margin_pct), { readonly: true, theme: T_TOTAL });
      }
      if (id === 'contribution_pct') {
        return gcell.text(fmtPct(totals.contribution_pct), { readonly: true, theme: T_TOTAL });
      }
      if (['quantity', 'gross_revenue', 'discount_amount', 'net_revenue', 'cost_of_sale', 'gross_profit',
        'secondary_cost', 'transfer_cost', 'warehouse_cost', 'logistics_cost', 'contribution'].includes(id)) {
        // Each cell is the honest sum of the column above it. The row does NOT
        // foot — net revenue includes SKUs with no cost, which therefore
        // contribute no cost and no profit. Forcing it to foot would mean
        // treating uncosted revenue as profit, which is the one thing this
        // report exists to avoid. The panel below explains it.
        const NEG = ['discount_amount', 'cost_of_sale', 'secondary_cost',
          'transfer_cost', 'warehouse_cost', 'logistics_cost'];
        const v = NEG.includes(id) ? -totals[id] : totals[id];
        return gcell.number(v, { display: fmt0(v), readonly: true, theme: T_TOTAL });
      }
      return gcell.text('', { readonly: true, theme: T_TOTAL });
    }

    const s = skus[row];
    if (!s) return gcell.text('', { readonly: true });
    const uncosted = s.cost_basis === 'none';
    const loss = !uncosted && s.gross_profit < 0;

    switch (id) {
      case 'stock_code':
        return gcell.text(s.stock_code, { readonly: true, theme: uncosted ? T_UNCOSTED : undefined });
      case 'description':
        // The marker rides on the line itself, so a reader scanning the grid
        // cannot miss why a margin is blank or a cost looks borrowed.
        return gcell.text(
          `${s.description}${uncosted ? '   ⚠ not costed' : s.cost_basis === 'other_site' ? "   ~ cost from another site" : ''}`,
          { readonly: true, theme: uncosted ? T_UNCOSTED : undefined }
        );
      case 'stock_group':
        return gcell.text(s.stock_group, { readonly: true, theme: T_MUTED });
      case 'quantity':
        return gcell.number(s.quantity, { display: fmt0(s.quantity), readonly: true });
      case 'gross_revenue':
        return gcell.number(s.gross_revenue, { display: fmt0(s.gross_revenue), readonly: true });
      case 'discount_amount':
        return gcell.number(-s.discount_amount, { display: fmt0(-s.discount_amount), readonly: true });
      case 'net_revenue':
        return gcell.number(s.net_revenue, { display: fmt0(s.net_revenue), readonly: true });
      case 'cost_of_sale':
        return gcell.number(-s.cost_of_sale, {
          display: uncosted ? '—' : fmt0(-s.cost_of_sale),
          readonly: true, theme: uncosted ? T_UNCOSTED : undefined,
        });
      case 'gross_profit':
        return gcell.number(s.gross_profit, {
          display: uncosted ? '—' : fmt0(s.gross_profit),
          readonly: true, theme: loss ? T_LOSS : uncosted ? T_UNCOSTED : undefined,
        });
      case 'margin_pct':
        return gcell.text(fmtPct(s.margin_pct), {
          readonly: true, theme: loss ? T_LOSS : s.margin_pct == null ? T_UNCOSTED : undefined,
        });
      // Freight is known whether or not the SKU is costed, so these render as
      // real numbers on uncosted lines too — unlike cost of sale, which is
      // genuinely absent there.
      case 'secondary_cost':
        return gcell.number(-s.secondary_cost, { display: fmt0(-s.secondary_cost), readonly: true });
      case 'transfer_cost':
        return gcell.number(-s.transfer_cost, { display: fmt0(-s.transfer_cost), readonly: true });
      case 'warehouse_cost':
        return gcell.number(-s.warehouse_cost, { display: fmt0(-s.warehouse_cost), readonly: true });
      case 'logistics_cost':
        return gcell.number(-s.logistics_cost, {
          display: fmt0(-s.logistics_cost), readonly: true,
          // An unrouted SKU shows zero freight because nothing was configured,
          // not because delivery is free. Mark it rather than let it read as
          // the cheapest line on the page.
          theme: s.unrouted ? T_UNCOSTED : undefined,
        });
      case 'contribution':
        return gcell.number(s.contribution || 0, {
          display: s.contribution == null ? '—' : fmt0(s.contribution),
          readonly: true,
          theme: s.contribution != null && s.contribution < 0 ? T_LOSS
            : uncosted ? T_UNCOSTED : undefined,
        });
      case 'contribution_pct':
        return gcell.text(fmtPct(s.contribution_pct), {
          readonly: true,
          theme: s.contribution_pct != null && s.contribution_pct < 0 ? T_LOSS
            : s.contribution_pct == null ? T_UNCOSTED : undefined,
        });
      case 'logistics_per_unit':
        return gcell.number(s.logistics_per_unit, {
          display: fmt2(s.logistics_per_unit), readonly: true,
          theme: s.unrouted ? T_UNCOSTED : undefined,
        });
      case 'contribution_per_unit':
        return gcell.number(s.contribution_per_unit || 0, {
          display: s.contribution_per_unit == null ? '—' : fmt2(s.contribution_per_unit),
          readonly: true,
          theme: s.contribution_per_unit != null && s.contribution_per_unit < 0 ? T_LOSS
            : uncosted ? T_UNCOSTED : undefined,
        });
      case 'price_per_unit':
        return gcell.number(s.price_per_unit, { display: fmt2(s.price_per_unit), readonly: true });
      case 'cost_per_unit':
        return gcell.number(s.cost_per_unit, {
          display: uncosted ? '—' : fmt2(s.cost_per_unit), readonly: true,
          theme: uncosted ? T_UNCOSTED : undefined,
        });
      case 'profit_per_unit':
        return gcell.number(s.profit_per_unit || 0, {
          display: s.profit_per_unit == null ? '—' : fmt2(s.profit_per_unit),
          readonly: true, theme: loss ? T_LOSS : uncosted ? T_UNCOSTED : undefined,
        });
      default:
        return gcell.text('', { readonly: true });
    }
  }, [colIds, skus, totals]);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to run this report.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Compact chrome — the report is the page. */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate(`/reports/${sectionCode || 'REV_MGMT'}`)}>
              <ArrowLeft className="w-3.5 h-3.5" /> Revenue Management
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              SKU Profitability
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            <span className="text-xs text-slate-400">{selectedSite || 'All sites'}</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {totals && (
              <>
                <Figure label="Net revenue" value={fmt0(totals.net_revenue)} />
                <Figure label="Gross profit" value={fmt0(totals.gross_profit)}
                        sub={`${fmtPct(totals.margin_pct)} of costed revenue`}
                        tone={totals.gross_profit >= 0 ? 'good' : 'bad'} />
                <Figure label="Logistics" value={fmt0(-totals.logistics_cost)}
                        sub="delivery + inter-depot + handling" />
                <Figure label="Contribution" value={fmt0(totals.contribution)}
                        sub={`${fmtPct(totals.contribution_pct)} of costed revenue`}
                        tone={totals.contribution >= 0 ? 'good' : 'bad'} />
              </>
            )}
            <ComparePicker reportCode="SKU_PROFIT" value={compare} onChange={setCompare} />
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm font-semibold text-red-700 dark:text-red-300">The report could not be run.</p>
              <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">{error}</p>
              <button className={`${btn} mt-4`} onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Try again</button>
            </div>
          ) : skus.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              No sales volumes for {selectedVersion}{selectedSite ? ` at ${selectedSite}` : ''}, so there is nothing to
              measure. Revenue &rsaquo; Budget Setup is where volumes are captured.
            </p>
          ) : (
            <MantleDataGrid
              gridId="report-sku-profit"
              fitHeight
              fitHeightPadding={6}
              columns={columns}
              rowCount={rowCount}
              getCellContent={getCellContent}
              freezeColumns={1}
              hint="Ranked by gross profit. Brackets are deductions. Ctrl+F searches, and columns drag to resize. Read-only: every figure comes from the budget that produced it."
              toolbar={
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {skus.length} SKU{skus.length === 1 ? '' : 's'} · GP% is over costed revenue only
                </span>
              }
            />
          )}
        </div>

        {/* Below the numbers, like the P&L. What the reader has to know before
            quoting any of this in a meeting. */}
        {caveats && !loading && !error && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
              <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">How to read this</h2>
            </div>
            <ul className="m-0 p-0 list-none divide-y divide-slate-100 dark:divide-slate-700/60">
              {caveats.uncosted_count > 0 && (
                <li className="px-4 py-2.5 border-l-4 border-l-amber-400 bg-amber-50/40 dark:bg-amber-500/5">
                  <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">
                    <TriangleAlert className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-600 dark:text-amber-400" />
                    {caveats.uncosted_count} SKU{caveats.uncosted_count === 1 ? '' : 's'} have no cost —
                    {' '}{caveats.uncosted_share_pct.toFixed(0)}% of net revenue
                  </p>
                  <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                    Their margin reads &ldquo;—&rdquo;, not 100%. <strong>Gross profit and GP% both cover costed
                    revenue only</strong> — {fmt0(caveats.uncosted_net_revenue)} of net revenue has no cost behind it
                    and no profit figure, so the TOTAL row does not foot: those SKUs contribute revenue and no cost.
                    Making it foot would mean booking uncosted revenue as pure profit.
                  </p>
                  <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 break-words">
                    {caveats.uncosted_codes.join(' · ')}
                  </p>
                  <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    These need a costed bill of materials — Manufacturing &rsaquo; Bill of Materials, then re-run costing.
                  </p>
                </li>
              )}
              {caveats.loss_making_count > 0 && (
                <li className="px-4 py-2.5 border-l-4 border-l-red-500 bg-red-50/40 dark:bg-red-500/5">
                  <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">
                    {caveats.loss_making_count} SKU{caveats.loss_making_count === 1 ? ' is' : 's are'} budgeted to sell
                    below cost
                  </p>
                  <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                    {caveats.loss_making_codes.join(' · ')}
                  </p>
                </li>
              )}
              {caveats.other_site_cost_count > 0 && (
                <li className="px-4 py-2.5">
                  <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">
                    {caveats.other_site_cost_count} SKU{caveats.other_site_cost_count === 1 ? '' : 's'} costed from
                    another site
                  </p>
                  <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                    A finished good is costed where it is made and sold where it is sold. Where the selling site has no
                    cost of its own, the SKU&rsquo;s average unit cost across the sites that did cost it is used.
                  </p>
                  <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                    {caveats.other_site_codes.join(' · ')}
                  </p>
                </li>
              )}
              {caveats.unrouted_count > 0 && (
                <li className="px-4 py-2.5">
                  <p className="m-0 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    <TriangleAlert className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-600 dark:text-amber-400" />
                    {caveats.unrouted_count} SKU{caveats.unrouted_count === 1 ? '' : 's'} have no delivery lane —
                    {' '}{caveats.unrouted_share_pct.toFixed(0)}% of gross revenue
                  </p>
                  <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                    Their logistics cost shows as zero because <strong>nothing is configured</strong>, not because
                    delivery is free. Contribution on those lines is overstated by whatever it would actually cost
                    to ship them. {fmt0(caveats.unrouted_revenue)} of gross revenue is affected.
                  </p>
                  <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 break-words">
                    {caveats.unrouted_codes.join(' · ')}
                  </p>
                </li>
              )}
              <li className="px-4 py-2.5">
                <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">Logistics is allocated on space, not on cases</p>
                <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                  A truck is filled by space. Cost is spread across SKUs on <strong>pallet-equivalents</strong> —
                  each item&rsquo;s cases converted by its own cases-per-pallet — so a 15kg bag carries more freight
                  than a 1kg bag. Spreading on cases instead would charge both the same and quietly cross-subsidise
                  the small pack. {caveats.logistics_allocation}.
                </p>
                <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {caveats.logistics_excluded}. Contribution is stated over costed revenue only, on the same
                  population as gross profit.
                </p>
              </li>
              <li className="px-4 py-2.5">
                <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">Discounts are allocated, not measured</p>
                <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                  Discounts are agreed with a customer or a customer group, never with a SKU. They are spread
                  {' '}{caveats.discount_allocation}. A customer who buys one product carries the whole of their
                  discount on it.
                </p>
                <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {fmt0(caveats.discounts_allocated)} of {fmt0(caveats.discounts_in_version)} allocated
                  {caveats.discounts_unallocated
                    ? ` — ${fmt0(caveats.discounts_unallocated)} could not be placed and is NOT in the figures above.`
                    : ' — all of it.'}
                </p>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const Figure = ({ label, value, sub, tone }) => {
  const cls = tone === 'bad'
    ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
    : tone === 'good'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return (
    <div className={`inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${cls}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
      {sub && <span className="text-[10px] opacity-70">{sub}</span>}
    </div>
  );
};
